import React, { useState, useEffect } from 'react';
import PublicLayout from './PublicLayout';
import { Globe, ExternalLink, MapPin } from 'lucide-react';

const InstitutionsPage = () => {
    const [institutions, setInstitutions] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [selectedInstitution, setSelectedInstitution] = useState(null);

    useEffect(() => {
        fetchInstitutions();
    }, []);

    const fetchInstitutions = async () => {
        try {
            setLoading(true);
            const API_BASE = process.env.REACT_APP_API_URL;
            const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
            if (!API_BASE || !BACKEND_URL) {
                console.error('API URLs not configured');
                return;
            }
            const response = await fetch(`${API_BASE}/institutions/public`);
            const data = await response.json();

            if (data.success) {
                // Map institutions with proper image URLs
                const institutionsWithImages = data.data.map(inst => ({
                    ...inst,
                    logo: inst.logo ? (inst.logo.startsWith('http') ? inst.logo : `${BACKEND_URL}${inst.logo}`) : null,
                    flag: getFlagEmoji(inst.country)
                }));
                setInstitutions(institutionsWithImages);
            } else {
                setError('Failed to load institutions');
            }
        } catch (err) {
            console.error('Error fetching institutions:', err);
            setError('Failed to load institutions');
        } finally {
            setLoading(false);
        }
    };

    const getFlagEmoji = (country) => {
        const flags = {
            'Ghana': '🇬🇭', 'Egypt': '🇪🇬', 'Ethiopia': '🇪🇹', 'Liberia': '🇱🇷',
            'Lebanon': '🇱🇧', 'Nigeria': '🇳🇬', 'Chad': '🇹🇩', 'Angola': '🇦🇴',
            'Guinea': '🇬🇳', 'Kenya': '🇰🇪', 'South Africa': '🇿🇦', 'Tanzania': '🇹🇿',
            'Uganda': '🇺🇬', 'Rwanda': '🇷🇼', 'Cameroon': '🇨🇲', 'Senegal': '🇸🇳',
            'Morocco': '🇲🇦', 'Tunisia': '🇹🇳', 'Algeria': '🇩🇿', 'Zimbabwe': '🇿🇼'
        };
        return flags[country] || '🌍';
    };

    if (loading) {
        return (
            <PublicLayout>
                <div className="min-h-screen flex items-center justify-center" style={{ background: 'linear-gradient(135deg, #6B8E23 0%, #556B2F 100%)' }}>
                    <div className="text-center">
                        <div className="animate-spin rounded-full h-16 w-16 border-t-4 border-b-4 border-white mx-auto mb-4"></div>
                        <p className="text-white text-xl font-semibold">Loading institutions...</p>
                    </div>
                </div>
            </PublicLayout>
        );
    }

    if (error) {
        return (
            <PublicLayout>
                <div className="min-h-screen flex items-center justify-center" style={{ background: 'linear-gradient(135deg, #6B8E23 0%, #556B2F 100%)' }}>
                    <div className="text-center text-white">
                        <p className="text-xl font-semibold">{error}</p>
                        <button
                            onClick={fetchInstitutions}
                            className="mt-4 px-6 py-3 bg-white text-green-700 font-bold rounded-lg hover:bg-gray-100 transition"
                        >
                            Try Again
                        </button>
                    </div>
                </div>
            </PublicLayout>
        );
    }

    return (
        <PublicLayout>
            <style>{`
        @keyframes fadeIn {
          from { opacity: 0; }
          to { opacity: 1; }
        }
        @keyframes slideDown {
          from { transform: translateY(-30px); opacity: 0; }
          to { transform: translateY(0); opacity: 1; }
        }
        @keyframes slideUp {
          from { transform: translateY(30px); opacity: 0; }
          to { transform: translateY(0); opacity: 1; }
        }
        @keyframes fadeInUp {
          from { transform: translateY(20px); opacity: 0; }
          to { transform: translateY(0); opacity: 1; }
        }
        @keyframes scaleIn {
          from { transform: scale(0.9); opacity: 0; }
          to { transform: scale(1); opacity: 1; }
        }
        .animate-fadeIn { animation: fadeIn 0.8s ease-in; }
        .animate-slideDown { animation: slideDown 0.6s ease-out; }
        .animate-slideUp { animation: slideUp 0.6s ease-out; }
        .animate-fadeInUp { animation: fadeInUp 0.6s ease-out; }
        .animate-scaleIn { animation: scaleIn 0.5s ease-out; }
        .institution-card {
          transition: all 0.3s ease;
        }
        .institution-card:hover {
          transform: translateY(-5px);
          box-shadow: 0 10px 25px rgba(107, 142, 35, 0.25);
        }
        .flag-emoji {
          filter: drop-shadow(2px 2px 4px rgba(0,0,0,0.2));
        }
      `}</style>

            {/* Hero Header */}
            <div className="py-4 animate-fadeIn" style={{
                background: 'linear-gradient(135deg, #6B8E23 0%, #556B2F 100%)',
                position: 'relative',
                overflow: 'hidden'
            }}>
                <div className="container" style={{ position: 'relative', zIndex: 2 }}>
                    <div className="row justify-content-center text-center">
                        <div className="col-lg-8">
                            <h1 className="h2 fw-bold text-white mb-2 animate-slideDown">Institutions Offering Respiratory Therapy in Africa</h1>
                            <p className="text-white mb-0 animate-slideUp" style={{ opacity: 0.95, fontSize: '0.95rem' }}>
                                Universities and institutions providing respiratory therapy education across Africa
                            </p>
                        </div>
                    </div>
                </div>
            </div>

            {/* Institutions Grid */}
            <section className="py-4" style={{ background: 'linear-gradient(to bottom, #f8f9fa 0%, #ffffff 100%)' }}>
                <div className="container">
                    {institutions.length === 0 ? (
                        <div className="text-center py-5">
                            <Globe size={64} className="text-muted mb-3" />
                            <h3 className="text-muted">No institutions available at the moment</h3>
                            <p className="text-muted">Check back soon for updates on our expanding network!</p>
                        </div>
                    ) : (
                        <div className="row g-3">
                            {institutions.map((institution, index) => (
                                <div
                                    key={institution.id || index}
                                    className="col-12 col-sm-6 col-md-4 col-lg-3"
                                    style={{
                                        animation: `fadeInUp 0.6s ease-out ${index * 0.1}s both`
                                    }}
                                >
                                    <div
                                        className="card institution-card border-0 shadow-sm h-100"
                                        style={{ cursor: 'pointer' }}
                                        onClick={() => setSelectedInstitution(institution)}
                                    >
                                        {/* Header with ARCA Green */}
                                        <div
                                            className="p-3 text-center text-white"
                                            style={{
                                                background: 'linear-gradient(135deg, #6B8E23 0%, #556B2F 100%)'
                                            }}
                                        >
                                            <div className="flag-emoji" style={{ fontSize: '2.5rem', marginBottom: '0.3rem' }}>
                                                {institution.flag}
                                            </div>
                                            <h6 className="fw-bold mb-1" style={{ fontSize: '0.9rem' }}>{institution.name}</h6>
                                            <p className="mb-0" style={{ opacity: 0.9, fontSize: '0.8rem' }}>
                                                <MapPin size={14} className="me-1" style={{ verticalAlign: 'middle' }} />
                                                {institution.country}
                                            </p>
                                        </div>

                                        {/* Logo Section */}
                                        <div className="p-3 bg-light text-center" style={{ minHeight: '120px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                            {institution.logo ? (
                                                <img
                                                    src={institution.logo}
                                                    alt={institution.name}
                                                    style={{ maxHeight: '100px', maxWidth: '100%', objectFit: 'contain' }}
                                                    onError={(e) => e.target.style.display = 'none'}
                                                />
                                            ) : (
                                                <div className="text-muted" style={{ fontSize: '0.75rem' }}>No Logo</div>
                                            )}
                                        </div>

                                        {/* Details Button */}
                                        <div className="card-footer bg-transparent border-top p-2">
                                            <button
                                                className="btn btn-sm text-white w-100 d-flex align-items-center justify-content-center"
                                                style={{ backgroundColor: '#6B8E23', fontSize: '0.8rem' }}
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    setSelectedInstitution(institution);
                                                }}
                                            >
                                                View Details
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            </section>

            {/* Call to Action */}
            <section className="py-5" style={{ background: 'linear-gradient(135deg, #6B8E23 0%, #556B2F 100%)' }}>
                <div className="container">
                    <div className="row justify-content-center">
                        <div className="col-lg-8 text-center text-white">
                            <h2 className="h3 fw-bold mb-3">Is Your Institution Missing?</h2>
                            <p className="lead mb-4" style={{ opacity: 0.95 }}>
                                If your institution offers respiratory therapy programs, we'd love to include you in our directory!
                            </p>
                            <a
                                href="/contact"
                                className="btn btn-light btn-lg text-success fw-bold px-5 shadow"
                            >
                                Contact Us Today
                            </a>
                        </div>
                    </div>
                </div>
            </section>

            {/* Institution Details Modal */}
            {selectedInstitution && (
                <div
                    className="modal fade show d-block"
                    style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}
                    onClick={() => setSelectedInstitution(null)}
                >
                    <div
                        className="modal-dialog modal-dialog-centered modal-lg animate-scaleIn"
                        onClick={(e) => e.stopPropagation()}
                    >
                        <div className="modal-content border-0 shadow-lg">
                            <div
                                className="modal-header border-0 text-white"
                                style={{ background: 'linear-gradient(135deg, #6B8E23 0%, #556B2F 100%)' }}
                            >
                                <div>
                                    <h5 className="modal-title fw-bold">
                                        {selectedInstitution.flag} {selectedInstitution.name}
                                    </h5>
                                    <p className="mb-0 mt-1" style={{ opacity: 0.9 }}>
                                        <MapPin size={16} className="me-1" />
                                        {selectedInstitution.country}
                                    </p>
                                </div>
                                <button
                                    type="button"
                                    className="btn-close btn-close-white"
                                    onClick={() => setSelectedInstitution(null)}
                                ></button>
                            </div>
                            <div className="modal-body p-4">
                                {selectedInstitution.website && (
                                    <div>
                                        <h6 className="fw-bold mb-3" style={{ color: '#6B8E23' }}>Website</h6>
                                        <div>
                                            <a
                                                href={selectedInstitution.website}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                className="text-decoration-none d-flex align-items-center"
                                                style={{ color: '#6B8E23' }}
                                            >
                                                <ExternalLink size={18} className="me-2" />
                                                <strong>Visit Website</strong>
                                            </a>
                                        </div>
                                    </div>
                                )}
                            </div>
                            <div className="modal-footer border-0 bg-light">
                                <button
                                    type="button"
                                    className="btn btn-secondary"
                                    onClick={() => setSelectedInstitution(null)}
                                >
                                    Close
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </PublicLayout>
    );
};

export default InstitutionsPage;
